import * as preact from "preact";

import * as modalController from "modalController";
import readlangClient from "readlangClient";
import Dialog from "views/Dialog";
import UserStatsView from "views/UserStatsView";

type Props = {
  targetUserID: string;
  targetEmail: string;
  onMergeComplete: () => void;
};

type MergeResult = {
  userWordsUpdated: number;
  userBooksUpdated: number;
  booksUpdated: number;
};

type State = {
  query: string;
  sourceUser: { _id: string; email: string } | null;
  searching: boolean;
  merging: boolean;
  mergeResult: MergeResult | null;
  error: string | null;
};

export default class MergeUserDialog extends preact.Component<Props, State> {
  state: State = {
    query: "",
    sourceUser: null,
    searching: false,
    merging: false,
    mergeResult: null,
    error: null,
  };

  async searchUser() {
    if (!this.state.query.trim()) {
      return;
    }

    this.setState({ searching: true, error: null, sourceUser: null });

    try {
      const response = await readlangClient.request("GET", "/api/user/list", {
        query: this.state.query.trim(),
      });

      const users = response.data.users;
      if (users.length === 0) {
        this.setState({ error: "No user found", searching: false });
      } else if (users.length > 1) {
        this.setState({
          error: "Multiple users found, be more specific",
          searching: false,
        });
      } else {
        const user = users[0];
        if (user._id === this.props.targetUserID) {
          this.setState({
            error: "Cannot merge a user into themselves",
            searching: false,
          });
        } else {
          this.setState({ sourceUser: user, searching: false });
        }
      }
    } catch (error: any) {
      this.setState({
        error: error.jqXHR?.alreadyNotified
          ? "Connection problem. Please try again."
          : "Search failed",
        searching: false,
      });
    }
  }

  async performMerge() {
    const { sourceUser } = this.state;
    if (!sourceUser) {
      return;
    }

    this.setState({ merging: true, error: null });

    try {
      const response = await readlangClient.request(
        "POST",
        `/api/admin/user/${this.props.targetUserID}/merge`,
        { sourceUserID: sourceUser._id }
      );

      this.setState({ mergeResult: response.data, merging: false });
      this.props.onMergeComplete();
    } catch (error: any) {
      this.setState({
        // On a terminal failure the merge may have completed server-side
        error: error.jqXHR?.alreadyNotified
          ? "Connection problem — check whether the merge completed"
          : "Merge failed",
        merging: false,
      });
    }
  }

  render() {
    const { targetEmail } = this.props;
    const { query, sourceUser, searching, merging, mergeResult, error } =
      this.state;

    if (mergeResult) {
      return (
        <Dialog
          class="mergeUserDialog"
          heading="Merge Complete"
          actions={
            <button onClick={() => modalController.cancel()}>Close</button>
          }
        >
          <p>Successfully merged into {targetEmail}:</p>
          <ul>
            <li>{mergeResult.userWordsUpdated} user words transferred</li>
            <li>{mergeResult.userBooksUpdated} user books transferred</li>
            <li>{mergeResult.booksUpdated} owned books transferred</li>
          </ul>
        </Dialog>
      );
    }

    return (
      <Dialog class="mergeUserDialog" heading="Merge User Data" error={error}>
        <p>
          Search for a user account to merge into <strong>{targetEmail}</strong>
        </p>

        <div style={{ display: "flex", marginBottom: "15px" }}>
          <input
            type="text"
            placeholder="Enter email or object ID"
            value={query}
            onInput={(event) =>
              this.setState({ query: event.currentTarget.value })
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                this.searchUser();
              }
            }}
            disabled={searching || merging}
          />
          <button
            onClick={() => this.searchUser()}
            disabled={searching || merging}
          >
            {searching ? "Searching..." : "Search"}
          </button>
        </div>

        {sourceUser && (
          <div>
            <h4>Source Account: {sourceUser.email}</h4>
            <UserStatsView userID={sourceUser._id} />

            <p style={{ marginTop: "15px" }}>
              This will transfer all user words, user books, and owned books
              from <strong>{sourceUser.email}</strong> to{" "}
              <strong>{targetEmail}</strong>.
            </p>

            <button
              onClick={() => this.performMerge()}
              disabled={merging}
              style={{ marginTop: "10px" }}
            >
              {merging ? "Merging..." : `Merge into ${targetEmail}`}
            </button>
          </div>
        )}
      </Dialog>
    );
  }
}
